let defaultTimezone = 'Australia/Sydney';
let defaultLocale = 'en-AU';

export const setTenantTimePreferences = ({ baseTimezone, locale } = {}) => {
  if (typeof baseTimezone === 'string' && baseTimezone.trim()) {
    defaultTimezone = baseTimezone.trim();
  }
  if (typeof locale === 'string' && locale.trim()) {
    defaultLocale = locale.trim();
  }
};

export const getTenantBaseTimezone = () => defaultTimezone;
export const getTenantLocale = () => defaultLocale;

export const resolveTimezone = (timezone) => timezone || defaultTimezone;

const timezoneOffsetFormatters = new Map();
const dateTimeFormatters = new Map();
const shortDateFormatters = new Map();
const dateKeyFormatters = new Map();
const timezoneShortFormatters = new Map();

const getTimezoneOffsetFormatter = (timezone) => {
  const resolvedTimezone = resolveTimezone(timezone);
  if (!timezoneOffsetFormatters.has(resolvedTimezone)) {
    timezoneOffsetFormatters.set(
      resolvedTimezone,
      new Intl.DateTimeFormat('en-US', {
        timeZone: resolvedTimezone,
        timeZoneName: 'shortOffset',
        hour: '2-digit',
        minute: '2-digit',
      }),
    );
  }
  return timezoneOffsetFormatters.get(resolvedTimezone);
};

const getDateTimeFormatter = (timezone) => {
  const resolvedTimezone = resolveTimezone(timezone);
  const cacheKey = `${defaultLocale}:${resolvedTimezone}`;
  if (!dateTimeFormatters.has(cacheKey)) {
    dateTimeFormatters.set(
      cacheKey,
      new Intl.DateTimeFormat(defaultLocale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: resolvedTimezone,
      }),
    );
  }
  return dateTimeFormatters.get(cacheKey);
};

const getShortDateFormatter = (timezone) => {
  const resolvedTimezone = resolveTimezone(timezone);
  const cacheKey = `${defaultLocale}:${resolvedTimezone}`;
  if (!shortDateFormatters.has(cacheKey)) {
    shortDateFormatters.set(
      cacheKey,
      new Intl.DateTimeFormat(defaultLocale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: resolvedTimezone,
      }),
    );
  }
  return shortDateFormatters.get(cacheKey);
};

const getDateKeyFormatter = (timezone) => {
  const resolvedTimezone = resolveTimezone(timezone);
  if (!dateKeyFormatters.has(resolvedTimezone)) {
    dateKeyFormatters.set(
      resolvedTimezone,
      new Intl.DateTimeFormat('en-CA', {
        timeZone: resolvedTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    );
  }
  return dateKeyFormatters.get(resolvedTimezone);
};

const getTimezoneShortFormatter = (timezone) => {
  const resolvedTimezone = resolveTimezone(timezone);
  const cacheKey = `${defaultLocale}:${resolvedTimezone}`;
  if (!timezoneShortFormatters.has(cacheKey)) {
    timezoneShortFormatters.set(
      cacheKey,
      new Intl.DateTimeFormat(defaultLocale, {
        timeZone: resolvedTimezone,
        timeZoneName: 'short',
      }),
    );
  }
  return timezoneShortFormatters.get(cacheKey);
};

export const getTimezoneOffsetMinutes = (date, timezone) => {
  const formatter = getTimezoneOffsetFormatter(timezone);
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find((part) => part.type === 'timeZoneName');
  if (!tzPart) return 0;
  const match = tzPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2] || 0);
  const minutes = Number(match[3] || 0);
  return sign * (hours * 60 + minutes);
};

// Resolve the UTC instant for a target wall-clock time expressed as UTC
// (e.g. 09:30 becomes Date.UTC(y, m-1, d, 9, 30)) in the given timezone.
// Iterates the offset so times near DST transitions resolve correctly.
// Non-existent wall times (DST "gap") resolve deterministically to the
// later instant; ambiguous times ("fold") resolve to the offset in effect
// at the UTC guess, matching the server's behaviour.
const resolveZonedInstant = (utcGuess, timezone) => {
  let candidate = utcGuess - getTimezoneOffsetMinutes(new Date(utcGuess), timezone) * 60000;
  let previous = Number.NaN;
  for (let i = 0; i < 4; i += 1) {
    const offsetMinutes = getTimezoneOffsetMinutes(new Date(candidate), timezone);
    const next = utcGuess - offsetMinutes * 60000;
    if (next === candidate) break;
    if (next === previous) {
      candidate = Math.max(candidate, next);
      break;
    }
    previous = candidate;
    candidate = next;
  }
  return candidate;
};

export const createZonedDate = (dateString, timeString, timezone) => {
  if (!dateString || !timeString) return null;
  const [year, month, day] = dateString.split('-').map((part) => Number(part));
  const [hour, minute] = timeString.split(':').map((part) => Number(part));
  if ([year, month, day, hour, minute].some((value) => Number.isNaN(value))) {
    return null;
  }

  return new Date(
    resolveZonedInstant(Date.UTC(year, month - 1, day, hour, minute), timezone),
  );
};

export const formatDateTime = (value, timezone) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return getDateTimeFormatter(timezone).format(date);
};

export const formatShortDate = (value, timezone) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return getShortDateFormatter(timezone).format(date);
};

export const formatMonthYear = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(defaultLocale, {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export const formatLongDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(defaultLocale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export const formatTimezoneShort = (value, timezone) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = getTimezoneShortFormatter(timezone).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value || '';
};

export const formatToAmPm = (timeString) => {
  if (!timeString) return '';
  const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return timeString;
  let hh = parseInt(match[1], 10);
  const mm = match[2];
  const period = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12;
  if (hh === 0) hh = 12;
  return `${hh}:${mm} ${period}`;
};

// Return date key (YYYY-MM-DD) for a given Date in the target timezone.
export const getDateKeyInTimezone = (date = new Date(), timezone) => {
  // en-CA formatting gives YYYY-MM-DD which is convenient for comparisons
  return getDateKeyFormatter(timezone).format(date);
};

// Return a Date object corresponding to midnight (start of day) in the target timezone
// Example: startOfDayInTimezone('2025-02-04', 'Australia/Sydney') -> Date representing 2025-02-04T00:00:00 in Australia/Sydney expressed as a JS Date instance
export const startOfDayInTimezone = (dateString, timezone) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map((v) => Number(v));
  if (!year || !month || !day) return null;

  return new Date(resolveZonedInstant(Date.UTC(year, month - 1, day, 0, 0, 0), timezone));
};

export const parseDateKeyInTimezone = (dateString, timezone) =>
  startOfDayInTimezone(dateString, timezone);
